/**
 * Panorama rendering and camera configuration constants.
 */

import { Vector3 } from '@technology/web3d';

/** Render panorama behind other elements (negative = behind) */
export const PANORAMA_RENDER_ORDER = -1;

/** Default tile height in pixels when not provided by API */
export const PANORAMA_DEFAULT_TILE_HEIGHT = 512;

/** Radius of the panorama sphere in scene units */
export const PANORAMA_SPHERE_RADIUS = 5;

/** Field of view boundaries in degrees for equirectangular panorama */
export const PANORAMA_FOV = {
  top: 90,
  bottom: -90,
  left: -180,
  right: 180,
} as const;

/** Minimum level of detail to load (skips lower resolution tiles) */
export const PANORAMA_MIN_LOD_LEVEL = 2;

/** LOD adjustment factor (1 = normal, higher = more detail) */
export const PANORAMA_LOD_ADJUSTMENT = 1;

/**
 * Delay (ms) before the LOD analyzer starts loading tiles beyond root
 * after the camera enters a panorama's sphere center.  During fast
 * station switching each briefly-current model would otherwise fire
 * many high-LOD requests that saturate browser connections.
 * 0 = disabled (immediate loading, legacy behavior).
 */
export const PANORAMA_STABILIZATION_DELAY_MS = 250;

/** Whether tile URL levels are zero-based (true for new imaging API) */
export const PANORAMA_IS_URL_LEVEL_ZERO_BASED = true;

/** Enable debug mode for panorama rendering */
export const PANORAMA_DEBUG = false;

/** Whether tile origin is at lower-left (false = upper-left origin) */
export const PANORAMA_IS_LOWER_LEFT_ORIGIN = false;

/**
 * PanoramaControls lat value for the horizon.
 * Convention: 0 = +Z (up), π/2 = horizon, π = -Z (down).
 */
export const PANORAMA_HORIZON_LAT = Math.PI / 2; // eslint-disable-line no-magic-numbers

/** Three.js camera default forward direction (−Z). */
export const PANORAMA_CAMERA_LOCAL_FORWARD = new Vector3(0, 0, -1);
